import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RxUtils, Tr } from '@utils';
import { useService } from '@hooks';
import { Stock, stockService } from '@services';
import { Button, Stack, Tab, Tabs } from '@mui/material';
import { DarkCard, EntityDeleteConfirmationDialog } from '@components/common';
import { EmptyEntityPageLayout, PortalPageTitle } from '@components/portal/common';
import { StockListViewer } from '@components/portal/stock';
import { ReactComponent as Illustration } from '@assets/illustrations/no-stock-added.svg';

const paddingX = 9;
type Entity = Stock;
enum STOCK_TAB {
	ALL = 'ALL',
	AVAILABLE = 'AVAILABLE',
	OUT_OF_STOCK = 'OUT_OF_STOCK'
}

const filterByTab = (entities: Entity[], tab: STOCK_TAB): Entity[] => {
	return entities.filter(({ isAvailable }) => {
		if (tab === STOCK_TAB.AVAILABLE) return isAvailable;
		else if (tab === STOCK_TAB.OUT_OF_STOCK) return !isAvailable;
		else return true;
	});
};

export const StockList: FC = () => {
	const { t } = useTranslation('portal');
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState<STOCK_TAB>(STOCK_TAB.ALL);
	const [searchText, setSearchText] = useState('');
	const [searchResult, setSearchResult] = useState<Entity[]>([]);
	const [deletingEntities, setDeletingEntities] = useState<Entity[]>([]);
	const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
	const [filteredEntities, setFilteredEntities] = useState<Entity[]>([]);
	const { data: allEntities = [], trigger } = useService(() => stockService.getAll(), []);

	const onEdit = (d: Entity) => navigate(`./edit/${d.id}`);
	const onDelete = (d: Entity) => RxUtils.promisify(stockService.delete(d), trigger);
	const onDeleteMultiple = (d: Entity[]) => RxUtils.promisify(stockService.deleteMultiple(d), trigger);

	useEffect(() => {
		if (searchText) {
			RxUtils.promisify(stockService.search(searchText), setSearchResult);
		} else {
			setSearchResult([]);
		}
	}, [searchText]);

	useEffect(
		() => (setSelectedEntities([]), setFilteredEntities(filterByTab(searchText ? searchResult : allEntities, activeTab))),
		[searchResult, allEntities, activeTab]
	);

	return (
		<Stack direction="column" spacing={4} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5, overflow: 'hidden' }}>
			<PortalPageTitle
				paddingX={paddingX}
				title={<Tr.Portal path="stock.title" />}
				subtitle={
					<Tr.Portal
						path={allEntities?.length > 1 ? 'stock.subtitle-plural' : 'stock.subtitle'}
						options={{ length: allEntities?.length || 0 }}
					/>
				}
				displaySearchbox
				searchboxProps={{
					testId: 'stock-search-box',
					placeholder: t('stock.search-placeholder'),
					searchText,
					onSearchTextChange: setSearchText
				}}
			/>
			<Stack direction="row" sx={{ paddingX }} spacing={5}>
				<DarkCard
					sx={{ flexGrow: 1 }}
					pattern="linepattern1"
					title={<Tr.Portal path="stock.add-single-stock-title" />}
					subtitle={<Tr.Portal path="stock.add-single-stock-subtitle" />}
					onClick={() => navigate('./add')}
				/>
				<DarkCard
					sx={{ flexGrow: 1 }}
					pattern="linepattern2"
					title={<Tr.Portal path="stock.bulk-update-title" />}
					subtitle={<Tr.Portal path="stock.bulk-update-subtitle" />}
					onClick={() => navigate('./bulk')}
				/>
			</Stack>
			<Stack direction="column" sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
				<Tabs value={activeTab} onChange={(_e, val) => setActiveTab(val as STOCK_TAB)} centered>
					<Tab value={STOCK_TAB.ALL} label={<Tr.Portal path="stock.tab-all" />} />
					<Tab value={STOCK_TAB.AVAILABLE} label={<Tr.Portal path="stock.tab-available" />} />
					<Tab value={STOCK_TAB.OUT_OF_STOCK} label={<Tr.Portal path="stock.tab-out-of-stock" />} />
				</Tabs>

				{Array.isArray(allEntities) && allEntities.length ? (
					<Stack direction="column" spacing={3} sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
						<StockListViewer
							showResultCount={!!searchText}
							paddingX={paddingX}
							entities={filteredEntities}
							selected={selectedEntities}
							empty={<Illustration />}
							onSelectChange={setSelectedEntities}
							onEdit={onEdit}
							onDelete={(entity) => setDeletingEntities([entity])}
						/>
						{selectedEntities.length ? (
							<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
								<Button
									data-testid="delete-stock-modal-open-button"
									variant="contained"
									color="secondary"
									sx={{ textTransform: 'uppercase' }}
									onClick={() => setDeletingEntities(selectedEntities)}
								>
									<Tr.Portal path="delete" />
								</Button>
								<Button
									data-testid="cancel-stock-selection-button"
									variant="outlined"
									color="secondary"
									sx={{ textTransform: 'uppercase' }}
									onClick={() => setSelectedEntities([])}
								>
									<Tr.Portal path="cancel" />
								</Button>
							</Stack>
						) : null}
					</Stack>
				) : (
					<EmptyEntityPageLayout illustration={<Illustration />} title={<Tr.Portal path="stock.no-stocks-added-title" />} />
				)}
			</Stack>
			<EntityDeleteConfirmationDialog
				open={!!deletingEntities?.length}
				content={<Tr.Portal path="stock.delete-confirmation-message" />}
				buttonProps={{ 'data-testid': 'stock-delete-confirmation-button' }}
				onClose={() => setDeletingEntities([])}
				onAccept={() => {
					deletingEntities.length > 1 ? onDeleteMultiple?.(deletingEntities) : onDelete?.(deletingEntities[0]);
					setDeletingEntities([]);
					setSelectedEntities([]);
				}}
			>
				{deletingEntities
					.map((entity) => `"${[entity.carModel?.brand, entity.carModel?.model].filter(Boolean).join(' ')}"`)
					.join(', ')}
			</EntityDeleteConfirmationDialog>
		</Stack>
	);
};
